import { DateTime } from 'luxon';
import ValidationInfo from './validation-info';
import { TranslationService } from '../services/translation.service';

export default class ValidationEngine {
  public static for(validationInfo: ValidationInfo): ValidationEngine {
    const instance = new ValidationEngine();
    instance.validationInfo = validationInfo;
    instance.foundErrors = [];
    return instance;
  }

  private validationInfo: ValidationInfo | undefined;
  private foundErrors: string[] = [];
  private skipValidation: boolean = false; // used by the if() method

  if(condition: boolean): ValidationEngine {
    this.skipValidation = !condition;
    return this;
  }

  endif(): ValidationEngine {
    this.skipValidation = false;
    return this;
  }

  // NV-6099: TranslationService.get.bind(TranslationService) --> Need to bind the translation service to the function to ensure it retains its context. Otherwise, it will result in an error when translation service is calling thins internally.
  public required(value: unknown, errorMessageTranslationKey?: string, translationFunc: (key: string) => string = TranslationService.get.bind(TranslationService)): ValidationEngine {
    if (this.skipValidation) return this;

    if (value === undefined || value === null || value === '') {
      this.foundErrors.push(translationFunc(errorMessageTranslationKey ?? 'common.validation.requiredfield'));
    }

    return this;
  }

  public requiredIf(
    condition: boolean,
    value: unknown,
    errorMessageTranslationKey?: string,
    translationFunc: (key: string) => string = TranslationService.get.bind(TranslationService)
  ): ValidationEngine {
    if (this.skipValidation) return this;

    if (condition && (value === undefined || value === null || value === '')) {
      this.foundErrors.push(translationFunc(errorMessageTranslationKey ?? 'common.validation.requiredfield'));
    }
    return this;
  }

  public notEmpty<T>(value: T[] | undefined | null, translationFunc: (key: string) => string = TranslationService.get.bind(TranslationService)) {
    if (this.skipValidation) return this;

    if (!value || value.length < 1) {
      this.foundErrors.push(translationFunc('common.validation.requiredfield'));
    }
    return this;
  }

  public email(value: string, translationFunc: (key: string) => string = TranslationService.get.bind(TranslationService)): ValidationEngine {
    if (this.skipValidation) return this;

    const regex = /^[a-zA-Z0-9_.+-]+@([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.[a-zA-Z0-9-]+$/;
    if (!regex.test(value)) {
      this.foundErrors.push(translationFunc('common.validation.email'));
    }
    return this;
  }

  public isAfter(value1: DateTime, value2: DateTime, errorMessageTranslationKey: string, translationFunc: (key: string) => string = TranslationService.get.bind(TranslationService)): ValidationEngine {
    if (this.skipValidation) return this;

    const isValid = value1.isValid && value2.isValid && value1 > value2;
    if (!isValid) this.foundErrors.push(translationFunc(errorMessageTranslationKey));
    return this;
  }

  public isBefore(value1: DateTime, value2: DateTime, errorMessageTranslationKey: string, translationFunc: (key: string) => string = TranslationService.get.bind(TranslationService)) {
    if (this.skipValidation) return this;

    const isValid = value1.isValid && value2.isValid && value1 < value2;
    if (!isValid) this.foundErrors.push(translationFunc(errorMessageTranslationKey));
    return this;
  }

  public maxLength(value: unknown[] | undefined, maxLength: number, translationFunc: (key: string) => string = TranslationService.get.bind(TranslationService)): ValidationEngine {
    if (this.skipValidation) return this;

    if (value && value.length > maxLength) {
      this.foundErrors.push(`${translationFunc('common.filelimitexceeded')} ${maxLength}`);
    }
    return this;
  }

  public lessThan(value: number, max: number, errorMessageTranslationKey: string, translationFunc: (key: string) => string = TranslationService.get.bind(TranslationService)): ValidationEngine {
    if (this.skipValidation) return this;

    if (value >= max) {
      this.foundErrors.push(translationFunc(errorMessageTranslationKey));
    }
    return this;
  }

  public greaterThan(value: number, min: number, errorMessageTranslationKey: string, translationFunc: (key: string) => string = TranslationService.get.bind(TranslationService)): ValidationEngine {
    if (this.skipValidation) return this;

    if (value <= min) {
      this.foundErrors.push(translationFunc(errorMessageTranslationKey));
    }
    return this;
  }

  public greaterThanOrEqual(
    value: number,
    min: number,
    errorMessageTranslationKey: string,
    translationFunc: (key: string) => string = TranslationService.get.bind(TranslationService)
  ): ValidationEngine {
    if (this.skipValidation) return this;

    if (value < min) {
      this.foundErrors.push(translationFunc(errorMessageTranslationKey));
    }
    return this;
  }

  public equal(value1: string, value2: string, errorMessageTranslationKey: string, translationFunc: (key: string) => string = TranslationService.get.bind(TranslationService)): ValidationEngine {
    if (this.skipValidation) return this;

    if (value1 !== value2) {
      this.foundErrors.push(translationFunc(errorMessageTranslationKey));
    }
    return this;
  }

  public notEqual(value1: string, value2: string, errorMessageTranslationKey: string, translationFunc: (key: string) => string = TranslationService.get.bind(TranslationService)): ValidationEngine {
    if (this.skipValidation) return this;

    if (value1 === value2) {
      this.foundErrors.push(translationFunc(errorMessageTranslationKey));
    }
    return this;
  }

  public validate(): void {
    if (!this.validationInfo) {
      throw new Error('ValidationInfo is not set');
    }
    this.validationInfo.validated = true;
    this.validationInfo.errors = this.foundErrors;
  }
}
